import React, {useState, useRef, MouseEvent} from 'react';
import {ButtonProps, RippleEffect} from './props';
import './style.css';
import {cn} from '@/utils/utils';

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  disabled = false,
  variant = 'default',
  size = 'md',
  className = '',
  style,
}) => {
  const [ripples, setRipples] = useState<RippleEffect[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nextRippleId = useRef(0);

  // 基础样式配置
  const baseClasses =
    'relative outline-none overflow-hidden font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95';

  // 变体样式
  const variantClasses = {
    default:
      'bg-white text-black border border-gray-300 hover:bg-gray-50 focus:ring-gray-300',
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
    outline:
      'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
  };

  // 尺寸样式
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  // 禁用状态样式
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed pointer-events-none'
    : 'cursor-pointer';

  // 处理点击事件并创建水波效果
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 2;

    const newRipple: RippleEffect = {
      x,
      y,
      size,
      id: nextRippleId.current++,
    };

    setRipples(prev => [...prev, newRipple]);

    // 动画完成后移除波纹效果
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 600);

    // 执行用户点击回调
    if (onClick) {
      onClick();
    }
  };

  const combinedClasses = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    disabledClasses,
    className,
  );
  return (
    <button
      ref={buttonRef}
      className={combinedClasses}
      style={style}
      onClick={handleClick}
      disabled={disabled}
      type="button">
      {/* 按钮内容 */}
      <span className="relative z-10">{children}</span>

      {/* 水波纹效果 */}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-c1 opacity-10 pointer-events-none animate-ping z-10"
          style={{
            left: ripple.x - ripple.size / 2,
            top: ripple.y - ripple.size / 2,
            width: ripple.size,
            height: ripple.size,
            animation: 'ripple 0.6s ease-out',
          }}
        />
      ))}
    </button>
  );
};
export default Button;
